import React, { useMemo } from 'react';

const formatTimestamp = (timestamp) => {
    if (!timestamp) {
        return 'Unknown time';
    }

    try {
        const date = new Date(timestamp);
        if (Number.isNaN(date.getTime())) {
            return String(timestamp);
        }
        return date.toLocaleString();
    } catch (e) {
        return String(timestamp);
    }
};

const getLevelColor = (level) => {
    switch (level?.toUpperCase()) {
        case 'ERROR':
            return '#dc3545';
        case 'WARNING':
        case 'WARN':
            return '#ffc107';
        case 'DEBUG':
            return '#17a2b8';
        case 'INFO':
        default:
            return '#64b5f6';
    }
};

const WebsiteAuditLogsModal = ({ isOpen, onClose, logs, isLoading }) => {
    const rows = useMemo(() => {
        if (!Array.isArray(logs)) {
            return [];
        }

        return logs.map((log, index) => {
            const level = log.level || 'INFO';
            const message = log.message || '';
            const timestamp = log.timestamp || null;
            const source = log.source || '';
            const additional = log.additional || {};

            return {
                id: `${level}-${index}-${message.slice(0, 16)}`,
                level,
                message,
                timestamp: formatTimestamp(timestamp),
                rawTimestamp: timestamp,
                source,
                additional
            };
        });
    }, [logs]);

    const sortedRows = useMemo(() => {
        return [...rows].sort((a, b) => {
            if (!a.rawTimestamp && !b.rawTimestamp) {
                return 0;
            }
            if (!a.rawTimestamp) {
                return 1;
            }
            if (!b.rawTimestamp) {
                return -1;
            }
            try {
                const aTime = new Date(a.rawTimestamp).getTime();
                const bTime = new Date(b.rawTimestamp).getTime();
                if (Number.isNaN(aTime) || Number.isNaN(bTime)) {
                    return 0;
                }
                return bTime - aTime; // Most recent first
            } catch (e) {
                return 0;
            }
        });
    }, [rows]);

    if (!isOpen) {
        return null;
    }

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(7, 7, 10, 0.92)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 10000,
                padding: '24px'
            }}
            onClick={onClose}
        >
            <div
                style={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: '1200px',
                    maxHeight: '860px',
                    backgroundColor: '#121212',
                    borderRadius: '10px',
                    boxShadow: '0 18px 60px rgba(0, 0, 0, 0.55)',
                    border: '1px solid rgba(100, 181, 246, 0.35)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '14px',
                        right: '14px',
                        backgroundColor: 'rgba(220, 53, 69, 0.92)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '50%',
                        width: '42px',
                        height: '42px',
                        fontSize: '24px',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontWeight: 'bold',
                        boxShadow: '0 8px 20px rgba(220, 53, 69, 0.45)',
                        transition: 'transform 0.2s ease, background-color 0.2s ease',
                        zIndex: 1
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.backgroundColor = 'rgba(207, 46, 60, 0.95)';
                        e.target.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'rgba(220, 53, 69, 0.92)';
                        e.target.style.transform = 'scale(1)';
                    }}
                >
                    ×
                </button>

                <div
                    style={{
                        flex: 1,
                        overflow: 'auto',
                        backgroundColor: '#0c0c0c',
                        padding: '24px'
                    }}
                >
                    <h3
                        style={{
                            margin: '0 0 24px 0',
                            color: '#e0e0e0',
                            fontWeight: '600'
                        }}
                    >
                        Website Audit Logs
                    </h3>

                    {isLoading ? (
                        <div
                            style={{
                                backgroundColor: '#181818',
                                borderRadius: '8px',
                                border: '1px solid rgba(100, 181, 246, 0.12)',
                                padding: '20px',
                                color: '#c3c3c3',
                                fontSize: '14px',
                                textAlign: 'center'
                            }}
                        >
                            Loading logs...
                        </div>
                    ) : sortedRows.length === 0 ? (
                        <div
                            style={{
                                backgroundColor: '#181818',
                                borderRadius: '8px',
                                border: '1px solid rgba(100, 181, 246, 0.12)',
                                padding: '20px',
                                color: '#c3c3c3',
                                fontSize: '14px'
                            }}
                        >
                            No audit logs found for this website.
                        </div>
                    ) : (
                        <div
                            style={{
                                backgroundColor: '#181818',
                                borderRadius: '8px',
                                border: '1px solid rgba(100, 181, 246, 0.12)',
                                padding: '20px'
                            }}
                        >
                            <div style={{ overflowX: 'auto' }}>
                                <table
                                    style={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        minWidth: '800px'
                                    }}
                                >
                                    <thead>
                                        <tr>
                                            <th style={headerStyle}>Level</th>
                                            <th style={headerStyle}>Timestamp</th>
                                            <th style={headerStyle}>Source</th>
                                            <th style={headerStyle}>Message</th>
                                            <th style={headerStyle}>Additional</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedRows.map((row) => (
                                            <tr key={row.id}>
                                                <td style={{ ...cellStyle, color: getLevelColor(row.level), fontWeight: '600' }}>
                                                    {row.level}
                                                </td>
                                                <td style={cellStyle}>{row.timestamp}</td>
                                                <td style={cellStyle}>{row.source || '-'}</td>
                                                <td
                                                    style={{
                                                        ...cellStyle,
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word'
                                                    }}
                                                >
                                                    {row.message}
                                                </td>
                                                <td style={cellStyle}>
                                                    {Object.keys(row.additional).length > 0 ? (
                                                        <pre style={{
                                                            margin: 0,
                                                            fontSize: '12px',
                                                            color: '#9ca3af',
                                                            whiteSpace: 'pre-wrap',
                                                            wordBreak: 'break-word'
                                                        }}>
                                                            {JSON.stringify(row.additional, null, 2)}
                                                        </pre>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const headerStyle = {
    textAlign: 'left',
    padding: '12px 16px',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: '#9a9a9a',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
    backgroundColor: '#1f1f1f'
};

const cellStyle = {
    padding: '12px 16px',
    fontSize: '13px',
    color: '#dcdcdc',
    borderBottom: '1px solid rgba(255, 255, 255, 0.04)'
};

export default WebsiteAuditLogsModal;


